
import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { supabase } from '@/integrations/supabase/client';
import { AlertCircle, CheckCircle } from 'lucide-react';

const AuthRedirect = () => {
  const [message, setMessage] = useState<{ title: string; description: string; type: 'success' | 'error' }>(); 
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleAuthRedirect = async () => {
      console.log("Auth redirect - starting processing");
      console.log("Location:", location);
      
      try {
        // Check if there's a session already
        const { data: { session } } = await supabase.auth.getSession();
        
        // Parse hash parameters from URL
        const hashParams = new URLSearchParams(location.hash.substring(1));
        const error = hashParams.get('error');
        const errorDescription = hashParams.get('error_description');
        
        if (error) {
          console.error('Auth error:', error, errorDescription);
          
          if (error === 'access_denied' && errorDescription?.includes('expired')) {
            setMessage({
              title: 'Verification link expired',
              description: 'The email verification link has expired. Please request a new verification email.',
              type: 'error'
            });
          } else {
            setMessage({
              title: 'Authentication error',
              description: errorDescription || 'An error occurred during authentication.',
              type: 'error'
            });
          }
        } else if (session) {
          // If we have a session, authentication was successful
          console.log("Auth redirect - session found", session.user?.id);
          setMessage({
            title: 'Authentication successful',
            description: 'You have been successfully signed in.',
            type: 'success'
          });
        } else {
          // Check if this is a successful OAuth redirect
          // In OAuth flow, we need to exchange the code for a session
          console.log("Auth redirect - checking for OAuth flow");
          
          // Allow a moment for the auth state to update
          setTimeout(async () => {
            const { data: { session: updatedSession } } = await supabase.auth.getSession();
            
            if (updatedSession) {
              console.log("Auth redirect - session established after delay");
              setMessage({
                title: 'Authentication successful',
                description: 'You have been successfully signed in.',
                type: 'success'
              });
            } else {
              // If after delay we still don't have a session, redirect to login
              console.log("Auth redirect - no session after delay, redirecting to login");
              navigate('/login');
              return;
            }
            setLoading(false);
          }, 1000);
          return;
        }
      } catch (error) {
        console.error("Error in auth redirect:", error);
        setMessage({
          title: 'Authentication error',
          description: 'An unexpected error occurred. Please try again.',
          type: 'error'
        });
      }
      
      setLoading(false);
    };

    handleAuthRedirect();
  }, [location, navigate]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-16rem)]">
        <div className="w-8 h-8 border-t-2 border-b-2 border-ai-indigo rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-16rem)] flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 bg-gray-50">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="text-2xl font-bold text-center">
            {message?.title}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Alert variant={message?.type === 'error' ? "destructive" : "default"} className="mb-4">
            {message?.type === 'error' ? (
              <AlertCircle className="h-4 w-4" />
            ) : (
              <CheckCircle className="h-4 w-4" />
            )}
            <AlertTitle>{message?.title}</AlertTitle>
            <AlertDescription>
              {message?.description}
            </AlertDescription>
          </Alert>
        </CardContent>
        <CardFooter className="flex justify-center">
          {message?.type === 'error' ? (
            <Button onClick={() => navigate('/login')} variant="outline">
              Return to login
            </Button>
          ) : (
            <Button onClick={() => navigate('/')} className="bg-gradient-to-r from-ai-indigo to-ai-purple">
              Go to homepage
            </Button>
          )}
        </CardFooter>
      </Card>
    </div>
  );
};

export default AuthRedirect;
